/**
 * Aktions- und Summenleiste über der Rechnungsliste.
 *
 * Erscheint, sobald mindestens eine Rechnung markiert ist, und liefert die drei
 * Werte, die im Online-Banking eingetippt werden — jeder einzeln kopierbar.
 * Die Rechenlogik steckt komplett in ./paymentBatch, hier steht nur Darstellung.
 */
import { useEffect, useState } from 'react'
import { AlertTriangle, Check, CheckCircle, Copy, Loader2, X } from 'lucide-react'
import { toast } from 'sonner'
import { formatCurrency } from './invoiceUtils'
import {
  buildPaymentBatch,
  formatAmountForClipboard,
  formatIban,
  SEPA_REFERENCE_MAX,
  type BatchInvoice,
} from './paymentBatch'

interface Props {
  /** Markierte Rechnungen in der Reihenfolge der Tabelle. */
  invoices: BatchInvoice[]
  onClear: () => void
  /** Schliesst die Sammelüberweisung ab (#130). Betraege rechnet der Server. */
  onMarkPaid: (ids: number[], applySkonto: boolean) => void
  markPaidPending: boolean
}

/**
 * Ein Wertfeld mit Kopierknopf. Der Haken bleibt kurz stehen, damit auch ohne
 * Blick auf den Toast erkennbar ist, welches Feld gerade kopiert wurde.
 */
function CopyField({
  label,
  display,
  clipboardValue,
  mono,
  disabledReason,
  hint,
  className = '',
}: {
  label: string
  display: string
  clipboardValue: string
  mono?: boolean
  disabledReason?: string
  hint?: string
  className?: string
}) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      // navigator.clipboard braucht einen sicheren Kontext; die WebUI läuft
      // unter HTTPS. Der Fallback deckt den Fall ab, dass der Browser die
      // Berechtigung verweigert — stilles Fehlschlagen wäre hier fatal, der
      // Nutzer würde einen alten Wert in sein Banking einfügen.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(clipboardValue)
      } else {
        const ta = document.createElement('textarea')
        ta.value = clipboardValue
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(ta)
        if (!ok) throw new Error('execCommand("copy") lieferte false')
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
      toast.success(`${label} kopiert`)
    } catch (err) {
      console.error(`[PaymentBatchBar] Kopieren fehlgeschlagen (${label}):`, err)
      toast.error(`${label} konnte nicht kopiert werden — bitte manuell markieren`)
    }
  }

  return (
    <div className={`min-w-0 ${className}`}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1">
        {label}
      </div>
      <div
        className={`flex items-center gap-2 rounded-md border bg-white px-2.5 py-1.5 ${
          disabledReason ? 'border-gray-200' : 'border-gray-300'
        }`}
      >
        <span
          className={`flex-1 min-w-0 truncate text-sm ${mono ? 'font-mono' : ''} ${
            disabledReason ? 'text-gray-400 italic' : 'text-gray-900'
          }`}
          title={disabledReason || display}
        >
          {disabledReason || display}
        </span>
        <button
          type="button"
          onClick={copy}
          disabled={!!disabledReason}
          title={disabledReason ? disabledReason : `${label} kopieren`}
          aria-label={`${label} kopieren`}
          className="flex-shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
      {hint && !disabledReason && (
        <div className="mt-1 text-[11px] text-gray-500">{hint}</div>
      )}
    </div>
  )
}

export default function PaymentBatchBar({ invoices, onClear, onMarkPaid, markPaidPending }: Props) {
  // Skonto ziehen ist die Vorgabe: eine laufende Frist ist bares Geld. Der
  // Schalter ist trotzdem da — wer strittige Positionen hat, zahlt voll.
  const [applySkonto, setApplySkonto] = useState(true)
  // Bezahlt-Markierung ist nicht mit einem Klick zurückzunehmen — deshalb ein
  // Zwischenschritt statt eines Sofort-Knopfs.
  const [confirmPaid, setConfirmPaid] = useState(false)
  const batch = buildPaymentBatch(invoices, { applySkonto })

  // Ändert sich die Auswahl, gilt die Bestätigung nicht mehr — sonst bucht ein
  // zweiter Klick auf einen anderen Stapel als den bestätigten.
  useEffect(() => { setConfirmPaid(false) }, [invoices.map((i) => i.id).join(','), applySkonto])

  if (batch.count === 0) return null

  const referenceTooLong = batch.referenceFull.length > SEPA_REFERENCE_MAX
  const noNumbers = batch.reference.length === 0

  const referenceDisabled = batch.recipientConflict
    ? 'Gesperrt — unterschiedliche Empfänger'
    : noNumbers
      ? 'Keine Rechnungsnummer hinterlegt'
      : undefined

  const ibanDisabled = batch.recipientConflict
    ? 'Gesperrt — unterschiedliche Empfänger'
    : !batch.iban
      ? 'Keine IBAN hinterlegt'
      : undefined

  return (
    <div className="mb-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <span className="text-sm text-gray-700">
          <span className="font-semibold">{batch.count}</span>{' '}
          {batch.count === 1 ? 'Rechnung' : 'Rechnungen'} ausgewählt
        </span>

        {batch.skontoCount > 0 && (
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={applySkonto}
              onChange={(e) => setApplySkonto(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>
              Skonto ziehen
              <span className="ml-1 text-emerald-700">
                (−{formatCurrency(batch.skontoTotal)} aus {batch.skontoCount}{' '}
                {batch.skontoCount === 1 ? 'Rechnung' : 'Rechnungen'})
              </span>
            </span>
          </label>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* #130: Abschluss der Überweisung. Bewusst NICHT durch den
              Empfänger-Konflikt gesperrt — man darf Rechnungen verschiedener
              Lieferanten als bezahlt markieren, auch wenn dafür mehrere
              Überweisungen nötig waren. */}
          {confirmPaid ? (
            <>
              <span className="text-sm font-medium text-gray-700">
                {batch.count} {batch.count === 1 ? 'Beleg' : 'Belege'} über{' '}
                {formatCurrency(batch.total)} als bezahlt buchen?
              </span>
              <button
                type="button"
                disabled={markPaidPending}
                onClick={() => onMarkPaid(invoices.map((inv) => inv.id), applySkonto)}
                className="inline-flex items-center gap-1.5 rounded-md border border-green-700 bg-green-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-green-800 disabled:opacity-50"
              >
                {markPaidPending
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <CheckCircle className="h-3.5 w-3.5" />}
                Ja, bezahlt
              </button>
              <button
                type="button"
                onClick={() => setConfirmPaid(false)}
                className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-900"
              >
                Abbrechen
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmPaid(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500 bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-600"
            >
              <CheckCircle className="h-3.5 w-3.5" />
              Als bezahlt markieren
            </button>
          )}
          <button
            type="button"
            onClick={onClear}
            className="flex items-center gap-1 text-sm text-gray-500 transition-colors hover:text-gray-700"
          >
            <X className="h-3 w-3" /> Auswahl aufheben
          </button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.4fr)]">
        <CopyField
          label="Verwendungszweck"
          display={batch.reference}
          clipboardValue={batch.reference}
          mono
          disabledReason={referenceDisabled}
          hint={
            batch.missingNumberCount > 0
              ? `${batch.missingNumberCount} ohne Rechnungsnummer — nicht enthalten`
              : `${batch.reference.length}/${SEPA_REFERENCE_MAX} Zeichen`
          }
        />

        <CopyField
          label="Gesamtsumme"
          display={formatCurrency(batch.total)}
          clipboardValue={formatAmountForClipboard(batch.total)}
          mono
          hint={
            applySkonto && batch.skontoTotal > 0
              ? `ohne Skonto ${formatCurrency(batch.openTotal)}`
              : undefined
          }
        />

        <CopyField
          label={batch.recipient ? `IBAN — ${batch.recipient}` : 'IBAN'}
          display={formatIban(batch.iban)}
          clipboardValue={batch.iban || ''}
          mono
          disabledReason={ibanDisabled}
          hint={
            batch.missingIbanCount > 0
              ? `${batch.missingIbanCount} der Rechnungen ohne eigene IBAN`
              : undefined
          }
        />
      </div>

      {(batch.recipientConflict || referenceTooLong) && (
        <div className="mt-3 space-y-1.5">
          {batch.recipientConflict && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
              <span>
                <strong>Unterschiedliche Empfänger in der Auswahl.</strong> Verwendungszweck
                und IBAN sind für eine Sammelüberweisung gesperrt — die Summe wird nur
                informativ angezeigt.
                {batch.distinctRecipients.length > 0 && (
                  <> Betroffen: {batch.distinctRecipients.join(', ')}.</>
                )}
              </span>
            </div>
          )}
          {referenceTooLong && !batch.recipientConflict && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
              <span>
                Der Verwendungszweck ist mit {batch.referenceFull.length} Zeichen länger als
                die {SEPA_REFERENCE_MAX} SEPA-Zeichen. Kopiert werden die ersten{' '}
                {batch.reference.split(', ').length} Rechnungsnummern —{' '}
                <strong>{batch.droppedNumbers.length} fehlen</strong>{' '}
                ({batch.droppedNumbers.join(', ')}). Besser auf zwei Überweisungen aufteilen,
                sonst kann der Lieferant den Rest nicht zuordnen.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
